import BaseModel from "lib/model";
import BaseCollection from "lib/collection";
import BrandUsers from "collections/brand-users";

const baseSwatchUrl = "//cdn1.nuorder.com/brand_color_swatch";

const Color = BaseModel.extend({
    idAttribute: "color",
    props: {
        color: { type: "string", required: true },
        swatch_type: {
            type: "string",
            values: ["file", "hex"],
            required: true,
        },
        value: { type: "string", required: true },
    },
    getImageSrc() {
        return this.swatch_type === "file" ? `${baseSwatchUrl}/${this.value}.jpg` : "";
    },
});

const Branding = BaseModel.extend({
    props: {
        linesheets: "string",
        splash: "string",
    },
});

const Colors = BaseCollection.extend({
    mainIndex: "color",
    model: Color,
});

const Banner = BaseModel.extend({
    idAttribute: "_id",
    props: {
        _id: { type: "string" },
        name: { type: "string", required: true },
        background_color: { type: "string", required: true },
        foreground_color: { type: "string", required: true },
        type: { type: "string", values: ["default", "customization"] },
    },
});

const Banners = BaseCollection.extend({
    model: Banner,
});

const Wholesale = BaseModel.extend({
    props: {
        legal_terms: { type: "string" },
        legal_files: { type: "array" },
        seats: { type: "number" },
        catalogs: { type: "array" },
        templates: { type: "array" },
        customer_groups: {
            type: "array",
            default: function () {
                return [];
            },
            required: true,
        },
        prebook_fields: {
            type: "array",
            default: function () {
                return [];
            },
            required: true,
        },
        barcodes: {
            type: "object",
            default: () => ({}),
            required: true,
        },
    },
    collections: {
        color_swatches: Colors,
        user_connections: BrandUsers,
        product_banners: Banners,
        branding: Branding,
    },
    derived: {
        activeTemplates: {
            deps: ["templates"],
            fn() {
                return this.templates.filter((template) => !template.draft);
            },
        },
    },
});

const Api = BaseModel.extend({
    props: {
        consumer_key: { type: "string" },
        consumer_secret_key: { type: "string" },
        enabled: { type: "boolean" },
        catalogs: { type: "array" },
        permissions: { type: "object" },
        tokens: {
            type: "array",
            default: function () {
                return [];
            },
        },
    },
    collections: {
        color_swatches: Colors,
        user_connections: BrandUsers,
        product_banners: Banners,
        branding: Branding,
    },
    derived: {
        activeTemplates: {
            deps: ["templates"],
            fn() {
                return this.templates.filter((template) => !template.draft);
            },
        },
    },
});

export const PaymentsModule = BaseModel.extend({
    props: {
        enabled: { type: "boolean" },
        linking_enabled: { type: "boolean" },
        accounts: {
            type: "array",
            default: function () {
                return [];
            },
        },
    },
});

const SalesRep = BaseModel.extend({
    idAttribute: "_id",
    props: {
        title: { type: "string" },
        name: { type: "string", required: true },
        email: { type: "string", required: true },
        phone: { type: "string" },
        _id: { type: "string" },
    },
    derived: {
        _initials: {
            cache: false,
            fn() {
                const fullName = this.name.split(" ");
                let initials;
                if (fullName.length > 1) {
                    initials = fullName[0][0] + fullName[1][0];
                } else {
                    initials = fullName[0][0];
                }

                return initials;
            },
        },
    },
});

const SalesReps = BaseCollection.extend({
    mainIndex: "_id",
    model: SalesRep,
});

const RetailCategory = BaseModel.extend({
    idAttribute: "_id",
    props: {
        name: { type: "string", required: true },
        subcategories: { type: "array" },
    },
});

const RetailCategories = BaseCollection.extend({
    mainIndex: "_id",
    model: RetailCategory,
});

const BuyerMarketplace = BaseModel.extend({
    props: {
        description: { type: "string" },
        enabled: { type: "boolean" },
        trial_info: { type: "string" },
        logo_image: { type: "string" },
        pricepoints: { type: "array" },
        events: { type: "array" },
        images: { type: "array" },
    },
    collections: {
        sales_reps: SalesReps,
        retail_categories: RetailCategories,
    },
});

const ShippingModule = BaseModel.extend({
    props: {
        enabled: { type: "boolean" },
    },
});

const AssortmentsModule = BaseModel.extend({
    props: {
        enabled: { type: "boolean" },
        merchant_default_warehouses: { type: "array" },
    },
});

const DriveModule = BaseModel.extend({
    props: {
        enabled: { type: "boolean" },
    },
});

const VerticalAssortmentsModule = BaseModel.extend({
    props: {
        enabled: { type: "boolean" },
    },
});

const AssortmentOrderIntentsModule = BaseModel.extend({
    props: {
        enabled: { type: "boolean" },
    },
});

const PacksModule = BaseModel.extend({
    props: {
        enabled: { type: "boolean" },
    },
});

const PreferredRetailer = BaseModel.extend({
    idAttribute: "_id",
    props: {
        _id: { type: "string" },
        preferred_retailer_id: { type: "string", required: true },
        company: { type: "string", required: true },
        company_name: { type: "string" },
    },
});

const PreferredRetailersModule = BaseCollection.extend({
    mainIndex: "_id",
    model: PreferredRetailer,
});

export default BaseModel.extend({
    children: {
        base: Wholesale,
        buyer_marketplace: BuyerMarketplace,
        api: Api,
        payments: PaymentsModule,
        shipping: ShippingModule,
        preferred_retailers: PreferredRetailersModule,
        assortments: AssortmentsModule,
        drive: DriveModule,
        packs: PacksModule,
        vertical_assortments: VerticalAssortmentsModule,
        assortment_order_intents: AssortmentOrderIntentsModule,
    },
    derived: {
        wholesale: {
            cache: false,
            fn() {
                return this.base;
            },
        },
    },
});
